import { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronRight, FolderKanban, LayoutGrid, Settings, UserCog } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../../store';
import { selectAuthUser, selectRoles } from '../../../store/slices/auth/auth.selector';
import { PRIVATE_PATHS } from '../../../routes/constants';
import { UserRole } from '../../../types/users';
// Drawn for these two entries rather than borrowed from the icon set, and
// stroked in `currentColor` so they take the hover like the rest.
import { ReactComponent as OrgSettingsIcon } from '../../../assets/org-settings-icon.svg';
import { ReactComponent as CrmSettingsIcon } from '../../../assets/crm-settings-icon.svg';

/** Sized to sit with the 18px icons either side of it. */
const ICON_CLASS = 'h-[18px] w-[18px] shrink-0';

/**
 * A module whose settings this menu can reach.
 *
 * `roles` is who may open it at all; a module nobody here can configure is left
 * off the list rather than shown and refused.
 */
interface SettingsModule {
  id: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  roles: UserRole[];
  /** Built with the org slug when the entry is rendered. */
  buildPath: (orgSlug: string) => string;
  /** Which route prefix marks this module as the one being looked at. */
  matches: (pathname: string) => boolean;
}

const ORG_ADMIN_ROLES: UserRole[] = ['ORG_OWNER', 'ORG_ADMIN'];

/** In the same order as the modules in the header. */
const SETTINGS_MODULES: SettingsModule[] = [
  {
    id: 'hrm',
    label: 'HRM',
    icon: UserCog,
    roles: [...ORG_ADMIN_ROLES, 'HRM_ADMIN'],
    buildPath: (orgSlug) =>
      PRIVATE_PATHS.HRMS.SETTINGS.LEAVE_ALLOCATION.replace(':orgSlug', orgSlug),
    matches: (pathname) => pathname.includes('/hrms/'),
  },
  {
    id: 'pmo',
    label: 'PMO',
    icon: FolderKanban,
    roles: [...ORG_ADMIN_ROLES, 'PMO_ADMIN'],
    // Holiday calendars come first: member availability is set up against them.
    buildPath: (orgSlug) =>
      PRIVATE_PATHS.PMO.SETTINGS.HOLIDAY_CALENDARS.replace(':orgSlug', orgSlug),
    matches: (pathname) => pathname.includes('/pmo/'),
  },
  {
    id: 'crm',
    label: 'CRM',
    icon: CrmSettingsIcon,
    roles: [...ORG_ADMIN_ROLES, 'CRM_ADMIN'],
    buildPath: (orgSlug) =>
      PRIVATE_PATHS.CRM.SETTINGS.TAGS.replace(':orgSlug', orgSlug),
    matches: (pathname) => pathname.includes('/crm/'),
  },
];

/**
 * The settings menu in the header: the organisation's own settings, and a
 * module's.
 *
 * Module Settings opens in place rather than sliding a second panel over the
 * first — there are only ever a handful of modules, and a panel that replaces
 * what the user was reading costs them their bearings for no gain.
 */
const SettingsMenu = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const authUser = useAppSelector(selectAuthUser);
  const userRoles = useAppSelector(selectRoles) ?? [];
  const orgSlug = authUser?.orgSlug ?? 'org';

  const [isOpen, setIsOpen] = useState(false);
  const [isModulesOpen, setIsModulesOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const canSeeOrgSettings = userRoles.some((role) =>
    ORG_ADMIN_ROLES.includes(role)
  );
  const modules = SETTINGS_MODULES.filter((module) =>
    module.roles.some((role) => userRoles.includes(role))
  );
  // A module admin can configure only their own module, so there is nothing to
  // choose between: Module Settings goes straight there. Org Owners and Admins
  // keep the list, as does anyone who administers more than one module.
  const soleModule =
    !canSeeOrgSettings && modules.length === 1 ? modules[0] : null;

  // Nothing to configure, nothing to open.
  const hasAnything = canSeeOrgSettings || modules.length > 0;

  const close = () => {
    setIsOpen(false);
    // The next open starts closed: the expansion is a step the user takes,
    // not a state the menu remembers for them.
    setIsModulesOpen(false);
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current && !containerRef.current.contains(target)) {
        close();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  if (!hasAnything) return null;

  const goTo = (path: string) => {
    navigate(path);
    close();
  };

  return (
    <div className="relative flex items-center" ref={containerRef}>
      <button
        type="button"
        aria-label="Settings"
        aria-expanded={isOpen}
        onClick={() => (isOpen ? close() : setIsOpen(true))}
        className={`relative p-2 rounded-full hover:bg-white/5 transition-all duration-200 ${
          isOpen ? 'text-white bg-white/10' : 'text-neutral-medium hover:text-neutral'
        }`}
      >
        <Settings size={22} />
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className="absolute top-full right-0 mt-2 w-64 bg-primary-light rounded-xl shadow-lg border border-[#3A3D45] overflow-hidden z-50"
          >
            <div className="py-2">
              {canSeeOrgSettings && (
                <button
                  type="button"
                  onClick={() =>
                    goTo(
                      PRIVATE_PATHS.CORE.SETTINGS.ORGANIZATION.replace(
                        ':orgSlug',
                        orgSlug
                      )
                    )
                  }
                  className="group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#3A3D45]"
                >
                  <OrgSettingsIcon
                    className={`${ICON_CLASS} text-gray-400 transition-colors group-hover:text-white`}
                  />
                  <span className="text-sm transition-colors group-hover:text-white">
                    Org Settings
                  </span>
                </button>
              )}

              {modules.length > 0 && (
                <>
                  <button
                    type="button"
                    aria-expanded={soleModule ? undefined : isModulesOpen}
                    onClick={() =>
                      soleModule
                        ? goTo(soleModule.buildPath(orgSlug))
                        : setIsModulesOpen((open) => !open)
                    }
                    className={`group flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-[#3A3D45] ${
                      canSeeOrgSettings ? 'border-t border-primary-subtle' : ''
                    }`}
                  >
                    <LayoutGrid
                      size={18}
                      className="text-gray-400 transition-colors group-hover:text-white"
                    />
                    <span className="flex-1 text-sm transition-colors group-hover:text-white">
                      Module Settings
                    </span>
                    {/* Points the way in, then down at what it opened. A
                        direct link opens nothing, so it goes without, like
                        Org Settings. */}
                    {!soleModule && (
                      <motion.span
                        animate={{ rotate: isModulesOpen ? 90 : 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="flex text-gray-400 transition-colors group-hover:text-white"
                      >
                        <ChevronRight size={16} />
                      </motion.span>
                    )}
                  </button>

                  <AnimatePresence initial={false}>
                    {!soleModule && isModulesOpen && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="border-t border-primary-subtle py-1">
                          {modules.map((module, index) => {
                            const isCurrent = module.matches(location.pathname);

                            return (
                              <motion.button
                                key={module.id}
                                type="button"
                                initial={{ opacity: 0, x: -8 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{
                                  delay: index * 0.04,
                                  duration: 0.15,
                                }}
                                onClick={() => goTo(module.buildPath(orgSlug))}
                                className={`group flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-[#3A3D45] ${
                                  isCurrent ? 'bg-[#3A3D45]/60' : ''
                                }`}
                              >
                                <module.icon
                                  className={`${ICON_CLASS} transition-colors group-hover:text-white ${
                                    isCurrent ? 'text-white' : 'text-gray-400'
                                  }`}
                                />
                                <span
                                  className={`text-sm transition-colors group-hover:text-white ${
                                    isCurrent ? 'text-white' : ''
                                  }`}
                                >
                                  {module.label}
                                </span>
                              </motion.button>
                            );
                          })}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SettingsMenu;
